import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { iconoPorOrigen } from '../../utils/notificacionUtils';
import useCerrarConEscapeYClickAfuera from '../../hooks/useCerrarConEscapeYClickAfuera';
import useInertFondo from '../../hooks/useInertFondo';

const TABS = [
    { id: 'sistema', label: 'Sistema', icon: 'bi bi-bell-fill' },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'bi bi-whatsapp' },
    { id: 'anses', label: 'Expedientes', icon: 'bi bi-folder2-open' },
    { id: 'correo', label: 'Correo', icon: 'bi bi-envelope-fill' },
];

const ORIGEN_ICONO_CORREO = { GMAIL: 'bi-google', OUTLOOK: 'bi-microsoft' };
const ORIGEN_COLOR_CORREO = { GMAIL: '#EA4335', OUTLOOK: '#0078D4' };

function EstadoCarga() {
    return (
        <div className="mob-estado-generico">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <span>Cargando…</span>
        </div>
    );
}

function EstadoVacio({ icono, texto }) {
    return (
        <div className="mob-estado-generico">
            <i className={`bi ${icono}`} aria-hidden="true" />
            <span>{texto}</span>
        </div>
    );
}

function ItemNotificacion({ mensaje, fecha, leida, icono, color, onAbrir, onDescartar }) {
    return (
        <div
            className={`mob-campana-item ${!leida ? 'no-leida' : ''}`}
            role="button"
            tabIndex={0}
            onClick={onAbrir}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onAbrir();
                }
            }}
        >
            <span className="mob-campana-item-icono">
                <i className={`bi ${icono}`} style={{ color }} aria-hidden="true" />
            </span>
            <span className="mob-campana-item-texto">
                <span className="mob-campana-item-mensaje">{mensaje}</span>
                {fecha && (
                    <span className="mob-campana-item-fecha">
                        {fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        {' · '}
                        {fecha.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                )}
            </span>
            {onDescartar && (
                <button
                    type="button"
                    aria-label="Descartar"
                    className="mob-tocable"
                    onClick={(e) => { e.stopPropagation(); onDescartar(); }}
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        border: 'none', background: 'transparent', color: 'var(--mob-color-texto-mudo)',
                    }}
                >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
            )}
        </div>
    );
}

// Modal de detalle de evento — mismas clases que NotificacionCampana.js (desktop) y
// PushNotificationContainer.js, ya estilizadas globalmente (.event-dropdown-overlay/
// .event-dropdown), para no duplicar esa hoja de estilos acá.
function ModalEvento({ evento, onCerrar }) {
    return ReactDOM.createPortal(
        <div className="event-dropdown-overlay" onClick={onCerrar}>
            <div className="event-dropdown" onClick={(e) => e.stopPropagation()}>
                <div className="dropdown-header">
                    <h4>Detalles del Evento</h4>
                    <button className="dropdown-close-btn" onClick={onCerrar}>×</button>
                </div>
                <div className="event-details">
                    <div className="event-title"><h3>{evento.titulo}</h3></div>
                    <div className="event-info">
                        <div className="info-item">
                            <span className="info-label">📅 Fecha:</span>
                            <span className="info-value">{new Date(evento.fecha).toLocaleDateString()}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">🕐 Horario:</span>
                            <span className="info-value">
                                {evento.todoElDia ? 'Todo el día' : `${evento.horaInicio || '–'} a ${evento.horaFin || '–'}`}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">🏷️ Tipo:</span>
                            <span className="info-value">
                                {typeof evento.tipoEvento === 'string' ? evento.tipoEvento : evento.tipoEvento?.nombre || 'Sin tipo'}
                            </span>
                        </div>
                        {evento.idTramite && (
                            <div className="info-item">
                                <span className="info-label">📋 Trámite asociado:</span>
                                <span className="info-value">ID #{evento.idTramite}</span>
                            </div>
                        )}
                    </div>
                    {evento.descripcion && (
                        <div className="event-description mt-2">
                            <div className="descripcion-label">📝 Descripción:</div>
                            <div className="descripcion-text">{evento.descripcion}</div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

function TabSistema({ sistema, onCerrar }) {
    const { notificaciones, loading, abrirNotificacion, descartar, selectedEvent, closeEventModal } = sistema;

    return (
        <>
            {loading ? (
                <EstadoCarga />
            ) : notificaciones.length === 0 ? (
                <EstadoVacio icono="bi-bell-slash" texto="Sin notificaciones pendientes" />
            ) : (
                notificaciones.map((item) => {
                    const { icon, color } = iconoPorOrigen(item);
                    const leida = item?.leido === true;
                    return (
                        <ItemNotificacion
                            key={item.id}
                            mensaje={item.mensaje}
                            fecha={item.fechaProgramada ? new Date(item.fechaProgramada) : null}
                            leida={leida}
                            icono={icon}
                            color={leida ? '#94a3b8' : color}
                            onAbrir={() => abrirNotificacion(item, { onNavegar: onCerrar })}
                            onDescartar={() => descartar(item.id)}
                        />
                    );
                })
            )}
            {selectedEvent && <ModalEvento evento={selectedEvent} onCerrar={closeEventModal} />}
        </>
    );
}

function TabWhatsApp({ whatsapp, onCerrar }) {
    const { totalNoLeidos, abrirPanel } = whatsapp;
    return (
        <div className="mob-estado-generico">
            <i className="bi bi-whatsapp" aria-hidden="true" style={{ color: '#25D366' }} />
            <span>
                {totalNoLeidos > 0
                    ? `${totalNoLeidos > 99 ? '99+' : totalNoLeidos} mensaje${totalNoLeidos !== 1 ? 's' : ''} sin leer`
                    : 'Sin mensajes nuevos de WhatsApp'}
            </span>
            <button
                type="button"
                className="mob-boton-secundario mob-tocable"
                onClick={() => { abrirPanel(); onCerrar(); }}
            >
                Abrir mensajes
            </button>
        </div>
    );
}

function TabAnses({ anses, onCerrar }) {
    const { notificaciones, loading, nuevasCount, marcandoTodas, abrirNotificacion, marcarTodasLeidas, descartar } = anses;

    return (
        <>
            {nuevasCount > 0 && (
                <button
                    type="button"
                    className="mob-campana-accion-secundaria mob-tocable"
                    disabled={marcandoTodas}
                    onClick={marcarTodasLeidas}
                >
                    {marcandoTodas ? 'Marcando…' : 'Marcar todas como leídas'}
                </button>
            )}
            {loading ? (
                <EstadoCarga />
            ) : notificaciones.length === 0 ? (
                <EstadoVacio icono="bi-folder2" texto="Sin novedades de expedientes" />
            ) : (
                notificaciones.map((item) => (
                    <ItemNotificacion
                        key={item.id}
                        mensaje={item.mensaje}
                        fecha={item.fechaProgramada ? new Date(item.fechaProgramada) : null}
                        leida={item?.leido === true}
                        icono="bi-folder2-open"
                        color="#2563eb"
                        onAbrir={() => abrirNotificacion(item, { onNavegar: onCerrar })}
                        onDescartar={() => descartar(item.id)}
                    />
                ))
            )}
        </>
    );
}

function TabCorreo({ correo, onCerrar }) {
    const { emails, loading, abrirEmail, descartar } = correo;

    if (loading) return <EstadoCarga />;
    if (emails.length === 0) return <EstadoVacio icono="bi-envelope" texto="No hay correos recientes" />;

    return emails.map((item) => {
        const origen = item?.origen || 'GMAIL';
        return (
            <ItemNotificacion
                key={item.id}
                mensaje={item.mensaje}
                fecha={item.fechaProgramada ? new Date(item.fechaProgramada) : null}
                leida={item?.leido === true}
                icono={ORIGEN_ICONO_CORREO[origen] || ORIGEN_ICONO_CORREO.GMAIL}
                color={ORIGEN_COLOR_CORREO[origen] || ORIGEN_COLOR_CORREO.GMAIL}
                onAbrir={() => abrirEmail(item, { onNavegar: onCerrar })}
                onDescartar={() => descartar(item)}
            />
        );
    });
}

// Casilla de notificaciones a pantalla completa con pestañas por origen, que reemplaza a las
// 4 campanas independientes del header de escritorio (NotificacionCampana, MailCampana,
// NotificacionCampanaANSES y el botón inline de WhatsApp). Recibe los 4 resultados de hook ya
// cargados por NavegacionMobile — así el header (contador) y esta casilla leen la misma fuente
// sin fetchear dos veces. Mantiene el ruteo de cada tipo de notificación tal como está hoy.
export default function CampanaUnificada({ sistema, whatsapp, anses, correo, puedeAnses, onCerrar }) {
    const tabsDisponibles = TABS.filter((t) => t.id !== 'anses' || puedeAnses);
    const [tabActiva, setTabActiva] = useState('sistema');
    const contenedorRef = useRef(null);
    const tituloRef = useRef(null);

    useCerrarConEscapeYClickAfuera(contenedorRef, true, onCerrar);
    useInertFondo(true);

    useEffect(() => {
        const elementoPrevio = document.activeElement;
        tituloRef.current?.focus();
        return () => {
            if (elementoPrevio && typeof elementoPrevio.focus === 'function') {
                elementoPrevio.focus();
            }
        };
    }, []);

    const contarBadge = (id) => {
        if (id === 'sistema') return sistema.nuevasCount;
        if (id === 'whatsapp') return whatsapp.totalNoLeidos;
        if (id === 'anses') return anses.nuevasCount;
        if (id === 'correo') return correo.noLeidosCount;
        return 0;
    };

    return (
        <>
            <div className="mob-overlay-backdrop" />
            <div className="mob-sheet" ref={contenedorRef} role="dialog" aria-modal="true" aria-label="Notificaciones">
                <div className="mob-sheet-header">
                    <h2 className="mob-sheet-titulo" ref={tituloRef} tabIndex={-1}>Notificaciones</h2>
                    <button
                        type="button"
                        className="mob-sheet-boton-cerrar mob-tocable"
                        onClick={onCerrar}
                        aria-label="Cerrar notificaciones"
                    >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                    </button>
                </div>

                <div className="mob-campana-tabs" role="tablist">
                    {tabsDisponibles.map((tab) => {
                        const count = contarBadge(tab.id);
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={tabActiva === tab.id}
                                className={`mob-campana-tab mob-tocable ${tabActiva === tab.id ? 'activo' : ''}`}
                                onClick={() => setTabActiva(tab.id)}
                            >
                                <i className={tab.icon} aria-hidden="true" />
                                {tab.label}
                                {count > 0 && <span className="mob-campana-tab-badge">{count > 99 ? '99+' : count}</span>}
                            </button>
                        );
                    })}
                </div>

                <div className="mob-sheet-body">
                    {tabActiva === 'sistema' && <TabSistema sistema={sistema} onCerrar={onCerrar} />}
                    {tabActiva === 'whatsapp' && <TabWhatsApp whatsapp={whatsapp} onCerrar={onCerrar} />}
                    {tabActiva === 'anses' && puedeAnses && <TabAnses anses={anses} onCerrar={onCerrar} />}
                    {tabActiva === 'correo' && <TabCorreo correo={correo} onCerrar={onCerrar} />}
                </div>
            </div>
        </>
    );
}
